/** Shapes returned by the automations_* and events_* / forms_* operations (crm-automations). */

export type AutomationStatus = "draft" | "shadow" | "active" | "paused" | "archived";

export interface AutomationSummary {
  id: number;
  name: string;
  description: string | null;
  status: AutomationStatus;
  brand_id: number | null;
  keap_campaign_id: number | null;
  current_version_id: number | null;
  live_count: number;
  total_count: number;
  updated_at: string;
}

export interface Step {
  key: string;
  type: string;
  name?: string;
  next?: string;
  note?: string;
  subject?: string;
  branches?: { name?: string; next: string }[];
  else?: string;
  timeout?: string;
  timeout_next?: string;
}

export interface Trigger {
  key?: string;
  name?: string;
  type: string;
  start_at?: string;
}

export interface Goal {
  key: string;
  name?: string;
  action?: string;
  jump_to?: string;
}

export interface Definition {
  schema: number;
  settings: { brand_id?: number; reentry?: string; keap?: { campaign_id: number; sequences?: string[] } };
  triggers: Trigger[];
  goals?: Goal[];
  steps: Step[];
}

export interface GraphNode {
  id: string;
  kind: "trigger" | "step" | "goal" | "end";
  type?: string;
  key?: string;
  label: string;
  details?: string;
}

export interface AutomationDetail {
  automation: {
    id: number;
    name: string;
    description: string | null;
    status: AutomationStatus;
    brand_id: number | null;
    keap_campaign_id: number | null;
    current_version_id: number | null;
    updated_at: string;
  };
  definition: Definition | null;
  draft?: { version_id: number; version: number; saved_at: string; saved_by: string } | null;
  graph?: { nodes: GraphNode[]; edges: { from: string; to: string; label?: string; kind?: string }[]; names?: GraphNames };
  mermaid?: string;
  step_counts: { version_id: number; step_key: string; status: string; shadow: boolean; people: number }[];
  status_counts: Record<string, number>;
  versions: { id: number; version: number; notes: string | null; created_at: string; created_by: string; published_at: string | null; live_count: number }[];
  skipped_steps?: StepSkip[];
}

export interface Position {
  enrollment_id: number;
  automation_id: number;
  automation_name: string;
  automation_status: string;
  version: number;
  status: string;
  shadow: boolean;
  step_key: string;
  step_type: string;
  step_label: string;
  step_number: number;
  step_count: number;
  summary: string;
  step_entered_at: string | null;
  next_run_at: string | null;
  entered_at: string;
  completed_at: string | null;
  exit_reason: string | null;
  started_by: string;
  last_error?: string;
  /** Later steps this run will pass over (marked by staff). */
  skipping?: RunSkipMark[];
}

export interface RunSkipMark {
  step_key: string;
  step_label: string;
  subject?: string;
  by: string;
  at: string;
}

/** One step of a run's version, for "Skip this step" and "Move to step". */
export interface RunStep {
  key: string;
  number: number;
  type: string;
  label: string;
  subject?: string;
  current?: boolean;
  next: string;
  /** "this_run" (marked for this person), "everyone" (turned off for everyone), or absent. */
  skip?: "this_run" | "everyone";
  skip_by?: string;
  skippable: boolean;
}

export interface RunDetail {
  position: Position;
  log: LogEntry[];
  steps: RunStep[];
}

/** A step turned off for everyone (automations_step_skip). */
export interface StepSkip {
  step_key: string;
  reason?: string;
  by: string;
  at: string;
}

/** The preview automations_step_skip answers before turning a step off or on. */
export interface StepSkipPreview {
  action: string;
  step_key: string;
  step_label: string;
  step_type: string;
  subject?: string;
  people_at: number;
  people_before: number;
  shadow_at?: number;
  shadow_before?: number;
  turned_off_by?: string;
  turned_off_on?: string;
  what_happens: string;
  note?: string;
}

export interface EnrollmentRow {
  id: number;
  contact_id: number;
  contact_name: string;
  status: string;
  shadow: boolean;
  step_key: string;
  step_label: string;
  next_run_at: string | null;
  entered_at: string;
  completed_at: string | null;
  exit_reason: string | null;
}

export interface LogEntry {
  occurred_at: string;
  enrollment_id: number;
  automation_id: number;
  automation_name?: string;
  contact_id: number;
  step_key: string;
  step_type: string;
  step_label?: string;
  status: string;
  detail: Record<string, unknown>;
}

export interface CompareOut {
  crm_would_send: number;
  keap_sent: number;
  matched: number;
  match_rate: number;
  steps: { step_key: string; subject: string; crm_would_send: number; keap_sent: number; matched: number; crm_only: number; keap_only: number; median_delay_minutes: number }[];
  crm_only: { contact_id: number; step_key: string; subject: string; at: string }[];
  keap_only: { contact_id: number; step_key: string; subject: string; at: string }[];
  /** Each decision step checked against Keap: did Keap send the emails of the way the CRM chose? */
  decisions?: CompareDecision[];
  notes: string[];
}

export interface CompareDecision {
  step_key: string;
  name: string;
  runs: number;
  agree: number;
  disagree: number;
  unknown: number;
  /** agree / (agree + disagree); low means the assumed condition is wrong. */
  agree_rate: number;
  ways: { name: string; next: string; subjects: string[]; crm_took: number; keap_agreed: number }[];
  disagreements: { contact_id: number; crm_way: string; keap_way: string; at: string }[];
}

export interface KeapCampaignInfo {
  keap_campaign_id: number;
  name: string;
  brand_id: number;
  assumptions: string[];
  dropped: string[];
  steps: number;
  emails: number;
  automation_id?: number;
  status?: string;
}

export const BRANDS: Record<number, string> = { 1: "CREU", 2: "MHU", 3: "RVU", 4: "SSU", 5: "OBU" };

/** What the IDs in a definition stand for (keys are IDs as strings). The labels already show them: "Tag added: MHU Email List (420)". */
export interface GraphNames {
  tags?: Record<string, string>;
  emails?: Record<string, string>;
  email_versions?: Record<string, string>;
  keap_emails?: Record<string, string>;
  products?: Record<string, string>;
  events?: Record<string, string>;
  automations?: Record<string, string>;
}
